import type { TFunction } from 'i18next';
import { z } from 'zod';
import type {
  SaveVehicleMaintenanceRequest,
  VehicleMaintenanceItem,
} from '@/api/generated/schemas';

const codebookRefSchema = z.object({
  id: z.number(),
  name: z.string(),
  code: z.string().nullable(),
});

/**
 * Příloha ve formuláři. Uložená příloha má serverové `id` a `contentBase64 = null`; nově vybraný
 * soubor je naopak bez `id` a nese obsah, který se odešle až s uložením záznamu.
 */
const documentSchema = z.object({
  id: z.string().nullable(),
  fileName: z.string(),
  contentType: z.string(),
  contentBase64: z.string().nullable(),
});

/**
 * Factory Zod schématu servisního záznamu — single source of truth dialogu. Zrcadlí serverovou
 * validaci (`SaveVehicleMaintenanceValidator`); lokalizované hlášky se vkládají přímo do schématu,
 * proto se staví z `t` až za běhu.
 */
export function buildVehicleMaintenanceFormSchema(t: TFunction) {
  return z
    .object({
      id: z.string().nullable(),
      reference: z
        .string()
        .trim()
        .min(1, t('maintenance.validation.referenceRequired'))
        .max(50, t('maintenance.validation.referenceMaxLength')),
      type: codebookRefSchema.nullable(),
      startDate: z.string().min(1, t('maintenance.validation.startDateRequired')),
      endDate: z.string().min(1, t('maintenance.validation.endDateRequired')),
      description: z
        .string()
        .trim()
        .min(1, t('maintenance.validation.descriptionRequired'))
        .max(500, t('maintenance.validation.descriptionMaxLength')),
      // Cena jede přes textové pole jako ostatní číselná pole vozu — prázdná hodnota není nula.
      costPln: z.string().refine((raw) => {
        const value = Number(raw);
        return raw.trim() !== '' && Number.isFinite(value) && value >= 0;
      }, t('maintenance.validation.costNonNegative')),
      documents: z.array(documentSchema),
    })
    .superRefine((values, ctx) => {
      if (values.type == null) {
        ctx.addIssue({
          code: 'custom',
          path: ['type'],
          message: t('maintenance.validation.typeRequired'),
        });
      }
      if (values.startDate && values.endDate && values.endDate < values.startDate) {
        ctx.addIssue({
          code: 'custom',
          path: ['endDate'],
          message: t('maintenance.validation.dateRange'),
        });
      }
    });
}

export type VehicleMaintenanceFormValues = z.infer<
  ReturnType<typeof buildVehicleMaintenanceFormSchema>
>;
export type VehicleMaintenanceDocumentValue = VehicleMaintenanceFormValues['documents'][number];

export function emptyVehicleMaintenanceFormValues(): VehicleMaintenanceFormValues {
  return {
    id: null,
    reference: '',
    type: null,
    startDate: '',
    endDate: '',
    description: '',
    costPln: '0',
    documents: [],
  };
}

/** Mapuje uložený servisní záznam na hodnoty formuláře (režim editace). */
export function fromVehicleMaintenanceItem(
  item: VehicleMaintenanceItem
): VehicleMaintenanceFormValues {
  return {
    id: item.id,
    reference: item.reference,
    type: { id: item.typeCbId, name: item.typeName ?? '', code: null },
    startDate: item.startDate.slice(0, 10),
    endDate: item.endDate.slice(0, 10),
    description: item.description,
    costPln: String(item.costPln),
    documents: item.documents.map((document) => ({
      id: document.id,
      fileName: document.fileName,
      contentType: document.contentType,
      contentBase64: null,
    })),
  };
}

/** Sestaví tělo požadavku; `satisfies` hlídá shodu s BE kontraktem. */
export function toVehicleMaintenanceRequestBody(
  values: VehicleMaintenanceFormValues
): SaveVehicleMaintenanceRequest {
  return {
    id: values.id,
    reference: values.reference.trim(),
    startDate: values.startDate,
    endDate: values.endDate,
    typeCbId: values.type!.id,
    description: values.description.trim(),
    costPln: Number(values.costPln),
    documents: values.documents.map((document) => ({
      id: document.id,
      fileName: document.fileName,
      contentType: document.contentType,
      contentBase64: document.contentBase64,
    })),
  } satisfies SaveVehicleMaintenanceRequest;
}
